import Auid from 'Auid';
import BuildConfig from 'BuildConfig';
import { getProgramConfigValue, getProgramTypeConfig } from 'Program';
import { AdmissionOfferStatus } from 'AdmissionOffer';
import isNonRelayEmail from 'EmailInput/isNonRelayEmail';
import { type StatsigUser } from '@statsig/js-client';
import { BROWSER_AND_BUILD_INFO } from 'TinyEventLogger';
import { type CurrentUser } from './types';
import {
    getUserProgramState,
    getHasOfferOrIncludedOrCompleted,
    getIsCurrentOrHasCompletedProgram,
    getCohort,
} from './BaseUser';

// See also User::Statsig#as_statsig
export function asStatsig(user: CurrentUser | null | undefined): StatsigUser {
    if (!user) return { userID: Auid.ensure(), appVersion: BuildConfig.build_number };

    return {
        userID: user.id,
        appVersion: BuildConfig.build_number,
        custom: {
            // 'android' or 'ios' inside the Capacitor applications, or 'web' for everywhere else.
            // Note that this still leaves a hole, particularly in identifying mobile web on Android or iOS, but we've not yet needed to make that distinction.
            client: BROWSER_AND_BUILD_INFO.client,
            cordova: !!window.CORDOVA,
            created_at: user.createdAt,
            paid_student_conversion_status: getPaidStudentConversionStatus(user),
            sign_up_code: user.signUpCode || '',
        },
        privateAttributes: {
            email: user.email || '',
        },
    };
}

function getPaidStudentConversionStatus(user: CurrentUser) {
    const hasUpsThatSupportsApplications = !!user.userProgramStates.find(ups =>
        getProgramConfigValue(ups.programType, 'supportsProgramApplications'),
    );
    if (!hasUpsThatSupportsApplications) return 'other';

    // FIXME: we'd like to put admins into the 'other' category, but it's kinda
    // hard to get access to that on BaseUser right now, because CurrentUserIguanaObject and User don't store roles in the same way.
    // Totally solvable, but I'm trying to get this out.

    const hasAcceptedAdmissionOffer = !!user.admissionOffers.find(
        ao => ao.status === AdmissionOfferStatus.AcceptedAdmissionOffer,
    );

    return hasAcceptedAdmissionOffer ? 'committed' : 'prospective';
}

export const hasExternalInstitution = (user: CurrentUser | null | undefined) =>
    !!user?.institutions.some(institution => institution.external);

export function getActivePlaylistLocalePackId(user: CurrentUser | null | undefined) {
    if (!user) return null;

    const prefUps = getUserProgramState(user);

    return prefUps?.activePlaylistLocalePackId || user.activePlaylistLocalePackId;
}

export function inGroup(user: CurrentUser | null | undefined, groupName: string): boolean {
    if (!user) {
        return false;
    }

    return user.groups?.some(group => group.name === groupName) ?? false;
}

export function isSuperviewerUser(user: CurrentUser | null | undefined): boolean {
    return inGroup(user, 'SUPERVIEWER');
}

/**
 * Get the global role name for a user.
 * Based on the Angular implementation in user.js - finds the role with resource_id === null.
 */
export function getGlobalRoleName(user: CurrentUser | null | undefined): string | undefined {
    if (!user || !user.roles) {
        return undefined;
    }

    // Find the global role (role with resource_id === null)
    const globalRole = user.roles.find(role => role && role.resourceId === null && role.name !== 'cannot_create');

    return globalRole?.name;
}

export function hasAdminAccess(user: CurrentUser | null | undefined) {
    if (!user) return false;
    return getGlobalRoleName(user) === 'admin';
}

export function hasSuperEditorAccess(user: CurrentUser | null | undefined) {
    if (!user) return false;
    return hasAdminAccess(user) || getGlobalRoleName(user) === 'super_editor';
}

export function hasEditorAccess(user: CurrentUser | null | undefined) {
    if (!user) return false;
    return hasSuperEditorAccess(user) || getGlobalRoleName(user) === 'editor';
}

export function hasReportsAccess(user: CurrentUser | null | undefined) {
    if (!user) return false;
    return hasSuperEditorAccess(user) || user.isInstitutionalReportsViewer;
}

export function hasInterviewerAccess(user: CurrentUser | null | undefined) {
    if (!user) return false;
    return hasAdminAccess(user) || getGlobalRoleName(user) === 'interviewer';
}

export function hasSuperEditorOrInterviewerAccess(user: CurrentUser | null | undefined) {
    if (!user) return false;
    return hasSuperEditorAccess(user) || hasInterviewerAccess(user);
}

export function getHasEditCareerProfileAccess(user: CurrentUser | null): boolean {
    if (!user) return false;
    const programTypeConfig = getProgramTypeConfig(getUserProgramState(user)?.programType);
    if (!programTypeConfig) return false;
    return (
        (user.canEditCareerProfile || getHasOfferOrIncludedOrCompleted(user)) &&
        !!programTypeConfig.supportsEditingCareerProfile
    );
}

export function getMissingRequiredStudentNetworkEmail(user: CurrentUser | null): boolean {
    if (!user) return false;
    const isEmailValidForStudentNetwork = !isNonRelayEmail(user.email ?? '');
    return !!(
        user.prefStudentNetworkPrivacy === 'full' &&
        !user.studentNetworkEmail &&
        !isEmailValidForStudentNetwork &&
        user.hasAccessToFullStudentNetworkProfiles
    );
}

// Replicates the Iguana supportsNominationsTab + hasNominationsAccess logic.
// The original depends on ConfigFactory (Angular service) which isn't accessible here,
// so we replicate the production behavior using the hardcoded pilot cohort list.
// This is being removed entirely by PR #16673 (Nominations Tab Removal).
const PILOT_NOMINATIONS_COHORTS = ['EMBA54', 'EMBA46', 'MBA54', 'MBA46'];

export function getHasNominationsAccess(user: CurrentUser | null): boolean {
    if (!user) return false;
    const cohort = getCohort(user);
    if (!cohort) return false;
    return getIsCurrentOrHasCompletedProgram(user) && PILOT_NOMINATIONS_COHORTS.includes(cohort.name);
}
